import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useAtom, useStore } from 'jotai';
import { isSwitchingOrganizationAtom, organizationDataAtom } from '@/store';
import { organizationsQueryOptions } from '@/lib/query-options';
import { clearAllUserData } from '@/lib/auth-cleanup';
import fetchWithAuth from '@/lib/apis';
import { apiEndpoint } from '@/lib/consts';
import { OrganizationDataWithUsersMembership } from '@/lib/types';
import { useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/useToast';

/**
 * Minimum time the "Switching organization…" overlay stays visible. An org switch
 * can resolve in well under 100ms (cached/local data), which makes the loader
 * appear as a jarring flash. Holding the veil for at least this long makes the
 * transition read as a deliberate, smooth change.
 */
const SWITCH_OVERLAY_MIN_MS = 500;

/**
 * Org list + the secure organization switch.
 *
 * NOTE (#6449): this hook no longer seeds `organizationDataAtom`. The single
 * initialization path is the AppShell effect in `routes/_authenticated.tsx`,
 * which restores the org from the SERVER profile (`userProfileQueryOptions`)
 * — never from the JWT. Do not add a second seeding path here.
 */
export function useOrganizationInitialization() {
  const queryClient = useQueryClient();
  const router = useRouter();
  const store = useStore();
  const [organizationData, setOrganizationData] = useAtom(organizationDataAtom);
  // Shared across all hook instances so the switch overlay and the
  // double-switch guard behave identically wherever the switch is triggered.
  const [isSwitching, setIsSwitching] = useAtom(isSwitchingOrganizationAtom);
  const { toast } = useToast();

  // Fetch all organizations
  const { data: organizations, isLoading } = useQuery(organizationsQueryOptions());

  // Keep the atom in sync with the latest org list data (e.g. logo URL changes after a refetch).
  useEffect(() => {
    if (!organizations || !organizationData) return;
    const fresh = organizations.find((o) => o.id === organizationData.id);
    if (fresh) setOrganizationData(fresh);
  }, [organizations]);

  // Secure organization switching function. Returns true when the switch
  // committed; false when it was aborted (already switching, or the backend
  // refused the selection).
  const switchOrganization = useCallback(async (newOrg: OrganizationDataWithUsersMembership): Promise<boolean> => {
    if (isSwitching) {
      return false; // Prevent rapid switching
    }

    const switchStartedAt = Date.now();
    setIsSwitching(true);

    try {
      // Persist the selection FIRST and abort the local switch on failure
      // (#6449). The backend's org fallback (`current_user.last_organization_id`)
      // must never lag the UI: a failed PUT with a completed local switch would
      // leave every org-implicit request scoped to the previous org.
      const response = await fetchWithAuth(`${apiEndpoint}/organizations/${newOrg.id}/select`, {
        method: 'PUT',
      });
      if (!response.ok) {
        throw new Error(`Organization selection was rejected (${response.status})`);
      }

      // Leave entity-scoped pages (campaign/ad detail, etc.) BEFORE purging: the
      // object almost certainly doesn't exist in the new org, and a mounted
      // suspense query refetching into a 404 would throw the user at an error
      // boundary instead of their own campaigns (#6477).
      const currentPath = router.state.location.pathname;
      if (/^\/campaigns\/[^/]+\/.+/.test(currentPath)) {
        await router.navigate({ to: '/campaigns', replace: true });
      }

      // Org switch purges ALL org-scoped caches: query cache + registered atoms.
      clearAllUserData(queryClient, store);

      // Set the new organization
      setOrganizationData(newOrg);

      // Invalidate user profile query first to get updated last_organization_id
      await queryClient.invalidateQueries({ queryKey: ['user', 'profile'] });
      // Then invalidate all other queries
      await queryClient.invalidateQueries();
      return true;
    } catch (error) {
      console.error('Failed to switch organization:', error);
      toast({
        description: 'Failed to switch organization. The current organization is unchanged.',
        variant: 'destructive',
      });
      return false;
    } finally {
      const elapsed = Date.now() - switchStartedAt;
      if (elapsed < SWITCH_OVERLAY_MIN_MS) {
        await new Promise((resolve) => setTimeout(resolve, SWITCH_OVERLAY_MIN_MS - elapsed));
      }
      setIsSwitching(false);
    }
  }, [isSwitching, setIsSwitching, queryClient, router, store, setOrganizationData, toast]);

  return {
    organizations: organizations || [],
    organizationData,
    switchOrganization,
    isLoading: isLoading || isSwitching,
    isSwitching
  };
}
